import { useMemo, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableCollectionBinding,
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
  useCollectionBinding,
  useContentToken,
  useSlotGroup,
} from '@vibesites/runtime/react';
import type { Floorplan } from '@vibesites/schema';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal, useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useCtaSheenStyles } from '../hooks/useCtaSheenStyles';
import { parseBedCount } from '../utils/bedroomFormatting';

/**
 * Featured Floor Plan — spotlights a single plan on its own page.
 *
 * By default the plan with the most available units wins (fall back to the
 * largest, then the first enabled one). An editor can override that pick by
 * typing a plan name into the "Featured Plan Name" token — the first case-
 * insensitive name/code match replaces the auto pick.
 */

// ============================================================================
// Slot group + token definitions (module-level literals)
// ============================================================================

const scheduleCtaDef: SlotGroupDefinition = {
  label: 'Schedule a Tour CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/schedule-a-tour',
    },
    label: {
      label: 'Button Text',
      type: 'text',
      defaultValue: 'Schedule a Tour',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

const applyCtaDef: SlotGroupDefinition = {
  label: 'Apply Now CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/apply-now',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: {
      label: 'Button Text',
      type: 'text',
      defaultValue: 'Apply Now',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

const viewAllCtaDef: SlotGroupDefinition = {
  label: 'View All Floor Plans Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/floor-plans',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'View all floor plans',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

const tour3dDef: SlotGroupDefinition = {
  label: '3D Tour Link',
  fields: {
    url: {
      label: 'URL (Matterport, Realync, etc.)',
      type: 'url',
      defaultValue: '',
    },
    label: {
      label: 'Button Text',
      type: 'text',
      defaultValue: '3D Tour',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

// ----------------------------------------------------------------------------
// Image slot defs — one primary (defaults to the picked plan's diagram) plus
// two optional extras for floor plans that have multiple images (unit photos,
// alternate layouts, 3D rendering, etc.). sourceBinding points at the photo
// gallery so an editor's media picker swaps image + alt coherently when they
// pick a gallery item; they can also upload or paste a URL manually.
// ----------------------------------------------------------------------------

const primaryImageDef: SlotGroupDefinition = {
  label: 'Primary Image (defaults to the plan diagram)',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'floorplans[0].floorplan_image_url',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Floor plan diagram',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const image2Def: SlotGroupDefinition = {
  label: 'Image 2 (optional)',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: '',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: '',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const image3Def: SlotGroupDefinition = {
  label: 'Image 3 (optional)',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: '',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: '',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

// ============================================================================
// Formatting helpers
// ============================================================================

function toNumber(raw: string | number | null | undefined): number | null {
  if (raw == null || raw === '') return null;
  const n = parseFloat(String(raw).replace(/[$,]/g, ''));
  return Number.isNaN(n) ? null : n;
}

function formatBeds(plan: Floorplan): string {
  const beds = parseBedCount(plan.floorplan_beds_min);
  if (beds == null) return '—';
  return beds === 0 ? 'Studio' : String(beds);
}

function formatBaths(plan: Floorplan): string {
  const min = toNumber(plan.floorplan_baths_min);
  const max = toNumber(plan.floorplan_baths_max);
  if (min == null) return '—';
  if (max != null && max > min) return `${min} – ${max}`;
  return `${min}`;
}

function formatSqft(plan: Floorplan): string {
  const min = toNumber(plan.floorplan_sqft_min);
  const max = toNumber(plan.floorplan_sqft_max);
  if (min == null || min <= 0) return '—';
  if (max != null && max > min) {
    return `${min.toLocaleString()} – ${max.toLocaleString()}`;
  }
  return min.toLocaleString();
}

function formatPrice(plan: Floorplan): string {
  const min = toNumber(plan.floorplan_price_min);
  if (min == null || min <= 0) return 'Call for Pricing';
  return `Starting at $${Math.round(min).toLocaleString()}`;
}

function availableCount(plan: Floorplan): number {
  const n = toNumber(plan.floorplan_available_units);
  return n != null && n > 0 ? Math.floor(n) : 0;
}

/** Prefer the plan with the most available units, then biggest sqft, then first enabled. */
function pickFeatured(plans: Floorplan[], overrideName: string): Floorplan | null {
  const enabled = plans.filter((p) => !p.floorplan_is_disabled);
  if (!enabled.length) return null;

  const needle = overrideName.trim().toLowerCase();
  if (needle) {
    const match = enabled.find((p) => {
      const name = (p.floorplan_name || '').toLowerCase();
      const code = (p.floorplan_code || '').toLowerCase();
      return name === needle || code === needle || name.includes(needle) || code.includes(needle);
    });
    if (match) return match;
  }

  const sorted = [...enabled].sort((a, b) => {
    const availDiff = availableCount(b) - availableCount(a);
    if (availDiff !== 0) return availDiff;
    const sqftDiff = (toNumber(b.floorplan_sqft_max) ?? toNumber(b.floorplan_sqft_min) ?? 0) -
      (toNumber(a.floorplan_sqft_max) ?? toNumber(a.floorplan_sqft_min) ?? 0);
    if (sqftDiff !== 0) return sqftDiff;
    const priceDiff = (toNumber(b.floorplan_price_min) ?? 0) - (toNumber(a.floorplan_price_min) ?? 0);
    return priceDiff;
  });
  return sorted[0];
}

/** Resolves "/floor-plans?bedroom=one" style deep-links to the Floor Plans tab list. */
const BED_PARAM_KEYS = ['studio', 'one', 'two', 'three', 'four', 'five'];
function availabilityHref(plan: Floorplan): string {
  const beds = parseBedCount(plan.floorplan_beds_min);
  const key = beds == null ? null : BED_PARAM_KEYS[beds] ?? `${beds}`;
  return key ? `/floor-plans?bedroom=${key}` : '/floor-plans';
}

// ============================================================================
// Component
// ============================================================================

export function FeaturedFloorPlan() {
  useRevealStyles();
  useCtaSheenStyles();
  const sectionRef = useScrollReveal<HTMLElement>();
  const statsRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 90 });

  const floorplans = useCollectionBinding<Floorplan>('featuredfloorplan.items', {
    collection: 'floorplans',
  });
  const overrideName = useContentToken('featuredfloorplan.featured_name');

  const schedule = useSlotGroup('featuredfloorplan.schedule_cta', scheduleCtaDef);
  const apply = useSlotGroup('featuredfloorplan.apply_cta', applyCtaDef);
  const viewAll = useSlotGroup('featuredfloorplan.view_all_cta', viewAllCtaDef);
  const tour = useSlotGroup('featuredfloorplan.tour_3d', tour3dDef);
  // Only render the 3D tour pill when an editor has pasted a URL. Default is
  // empty so the button stays out of the way until a real tour exists.
  const tourUrl = (tour.url || '').trim();

  const plan = useMemo(
    () => pickFeatured(floorplans ?? [], overrideName || ''),
    [floorplans, overrideName],
  );

  // Image slots — overrides for the diagram, plus two optional extras. The
  // primary image falls back to the picked plan's `floorplan_image_url` at
  // runtime; the extras stay empty unless an editor fills them in.
  const primaryImage = useSlotGroup('featuredfloorplan.primary_image', primaryImageDef);
  const image2 = useSlotGroup('featuredfloorplan.image_2', image2Def);
  const image3 = useSlotGroup('featuredfloorplan.image_3', image3Def);

  // Index of the currently-displayed image in the gallery. Thumbnails below
  // the main image swap it. Reset to 0 whenever the gallery contents change
  // so a removed image doesn't leave the index pointing at empty space.
  const [activeIndex, setActiveIndex] = useState(0);

  // If the property has no floor plans at all, render the empty-state copy so
  // editors still see the section and lint anchors still parse.
  if (!plan) {
    return (
      <section ref={sectionRef} className="reveal-fade-up ffp-section">
        <div className="ffp-container">
          <EditableCollectionBinding
            bindingPath="featuredfloorplan.items"
            label="Floor Plans"
          >
            <RichTextContent
              path="featuredfloorplan.empty_message"
              label="Empty Message"
              defaultValue="<p>We haven’t featured a floor plan yet. Explore all of our floor plans or contact our leasing team for upcoming availability.</p>"
              as="div"
              className="ffp-empty"
            />
          </EditableCollectionBinding>
        </div>

        {/* Schema-lint anchors — hidden, never interactive */}
        <HiddenAnchors />

        <FeaturedFloorPlanStyles />
      </section>
    );
  }

  const name = plan.floorplan_name?.trim() || 'Featured Floor Plan';
  const beds = formatBeds(plan);
  const baths = formatBaths(plan);
  const sqft = formatSqft(plan);
  const price = formatPrice(plan);
  const available = availableCount(plan);
  const detailHref = availabilityHref(plan);

  // Build the gallery: slot overrides win, otherwise fall back to the picked
  // plan's diagram for slot #1. Slots #2 and #3 only appear if an editor has
  // set them. Each entry keeps its own groupPath so edit-mode highlighting
  // targets the right slot when hovering a thumbnail/the main image.
  const planImage = plan.floorplan_image_url || '';
  const gallery: Array<{
    url: string;
    alt: string;
    groupPath: 'featuredfloorplan.primary_image' | 'featuredfloorplan.image_2' | 'featuredfloorplan.image_3';
  }> = [
    {
      url: primaryImage.image || planImage,
      alt: primaryImage.alt || `${name} floor plan diagram`,
      groupPath: 'featuredfloorplan.primary_image',
    },
  ];
  if (image2.image) {
    gallery.push({
      url: image2.image,
      alt: image2.alt || `${name} — alternate view`,
      groupPath: 'featuredfloorplan.image_2',
    });
  }
  if (image3.image) {
    gallery.push({
      url: image3.image,
      alt: image3.alt || `${name} — alternate view`,
      groupPath: 'featuredfloorplan.image_3',
    });
  }
  // Clamp activeIndex so a just-removed image never leaves the pointer out of
  // bounds. Index is always valid because the primary slot is always present.
  const safeIndex = Math.min(activeIndex, gallery.length - 1);

  return (
    <section ref={sectionRef} className="reveal-fade-up ffp-section">
      <div className="ffp-container">
        <EditableCollectionBinding
          bindingPath="featuredfloorplan.items"
          label="Floor Plans"
        >
          <div className="ffp-grid">
            {/* Floor plan diagram + optional alternate images. Thumbnails
                swap the main view; the primary slot defaults to the picked
                plan's `floorplan_image_url` so this works out of the box.
                Each slot renders with its own literal groupPath so the
                schema linter can statically detect them. */}
            <div className="ffp-media">
              {/* All three slot wrappers always mount so edit-mode highlights
                  track each image's own slot. Only the active one is visible;
                  the others are display:none via the `is-hidden` class. */}
              <EditableSlotGroup
                groupPath="featuredfloorplan.primary_image"
                definition={primaryImageDef}
                className={`ffp-media-main${safeIndex === 0 ? '' : ' is-hidden'}`}
              >
                {gallery[0].url ? (
                  <VibesitesImage
                    url={gallery[0].url}
                    alt={gallery[0].alt}
                    className="ffp-media-image"
                    sizes="(max-width: 1023px) 100vw, 55vw"
                    priority
                  />
                ) : (
                  <div className="ffp-media-placeholder" aria-hidden="true" />
                )}
              </EditableSlotGroup>

              <EditableSlotGroup
                groupPath="featuredfloorplan.image_2"
                definition={image2Def}
                className={`ffp-media-main${safeIndex === 1 && image2.image ? '' : ' is-hidden'}`}
              >
                {image2.image ? (
                  <VibesitesImage
                    url={image2.image}
                    alt={image2.alt || `${name} — alternate view`}
                    className="ffp-media-image"
                    sizes="(max-width: 1023px) 100vw, 55vw"
                  />
                ) : (
                  <div className="ffp-media-placeholder" aria-hidden="true" />
                )}
              </EditableSlotGroup>

              <EditableSlotGroup
                groupPath="featuredfloorplan.image_3"
                definition={image3Def}
                className={`ffp-media-main${safeIndex === 2 && image3.image ? '' : ' is-hidden'}`}
              >
                {image3.image ? (
                  <VibesitesImage
                    url={image3.image}
                    alt={image3.alt || `${name} — alternate view`}
                    className="ffp-media-image"
                    sizes="(max-width: 1023px) 100vw, 55vw"
                  />
                ) : (
                  <div className="ffp-media-placeholder" aria-hidden="true" />
                )}
              </EditableSlotGroup>

              {available > 0 && (
                <span className="ffp-media-badge">{available} Available</span>
              )}

              {/* "View availability" link, stacked below the gallery so it
                  doesn't eat clicks on the editable image slots above. */}
              <a
                href={detailHref}
                className="ffp-media-detail-link"
                aria-label={`${name} availability`}
              >
                View availability →
              </a>

              {gallery.length > 1 && (
                <div className="ffp-thumbs" role="tablist" aria-label="Floor plan images">
                  {gallery.map((img, i) => (
                    <button
                      key={img.groupPath}
                      type="button"
                      role="tab"
                      aria-selected={i === safeIndex}
                      aria-label={`Show image ${i + 1} of ${gallery.length}`}
                      className={`ffp-thumb${i === safeIndex ? ' is-active' : ''}`}
                      onClick={() => setActiveIndex(i)}
                    >
                      <VibesitesImage
                        url={img.url}
                        alt=""
                        className="ffp-thumb-image"
                        sizes="80px"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Plan info + CTAs */}
            <div className="ffp-info">
              <h2 className="ffp-name">{name}</h2>

              <div ref={statsRef} className="ffp-stats">
                <div data-reveal className="reveal-fade-up ffp-stat">
                  <span className="ffp-stat-value">{beds}</span>
                  <span className="ffp-stat-label">{beds === 'Studio' ? '' : 'Bed'}</span>
                </div>
                <span className="ffp-stat-sep" aria-hidden="true" />
                <div data-reveal className="reveal-fade-up ffp-stat">
                  <span className="ffp-stat-value">{baths}</span>
                  <span className="ffp-stat-label">Bath</span>
                </div>
                <span className="ffp-stat-sep" aria-hidden="true" />
                <div data-reveal className="reveal-fade-up ffp-stat">
                  <span className="ffp-stat-value">{sqft}</span>
                  <span className="ffp-stat-label">Sq. Ft.</span>
                </div>
              </div>

              <p className="ffp-price">{price}</p>

              <RichTextContent
                path="featuredfloorplan.description"
                label="Description"
                defaultValue="<p>Thoughtfully laid out with the finishes residents love most, this plan brings together open living space, a dedicated kitchen, and comfortable bedrooms. Tour it in person or apply online to make it yours.</p>"
                as="div"
                className="ffp-description"
              />

              <div className="ffp-actions">
                <EditableSlotGroup
                  groupPath="featuredfloorplan.apply_cta"
                  definition={applyCtaDef}
                >
                  <a
                    href={apply.url || '/apply-now'}
                    target={apply._target || '_self'}
                    rel={apply._target === '_blank' ? 'noopener noreferrer' : undefined}
                    className="ffp-cta vantage-cta-sheen"
                  >
                    <span className="relative z-10">{apply.label || 'Apply Now'}</span>
                  </a>
                </EditableSlotGroup>

                <EditableSlotGroup
                  groupPath="featuredfloorplan.schedule_cta"
                  definition={scheduleCtaDef}
                >
                  <a
                    href={schedule.url || '/schedule-a-tour'}
                    target={schedule._target || '_self'}
                    className="ffp-cta ffp-cta--outline"
                  >
                    <span className="relative z-10">
                      {schedule.label || 'Schedule a Tour'}
                    </span>
                  </a>
                </EditableSlotGroup>

                {tourUrl && (
                  <EditableSlotGroup
                    groupPath="featuredfloorplan.tour_3d"
                    definition={tour3dDef}
                  >
                    <a
                      href={tourUrl}
                      target={tour._target || '_blank'}
                      rel={tour._target === '_blank' || !tour._target ? 'noopener noreferrer' : undefined}
                      className="ffp-tour-btn"
                      aria-label={tour.label || '3D Tour'}
                      title={tour.label || '3D Tour'}
                    >
                      <svg
                        className="ffp-tour-icon"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        {/* Isometric cube — reads as a "3D" affordance. */}
                        <path d="M12 2.5 3.5 7v10L12 21.5 20.5 17V7L12 2.5Z" />
                        <path d="M3.5 7 12 11.5 20.5 7" />
                        <path d="M12 11.5V21.5" />
                      </svg>
                      <span className="ffp-tour-label">
                        {tour.label || '3D Tour'}
                      </span>
                    </a>
                  </EditableSlotGroup>
                )}
              </div>

              <EditableSlotGroup
                groupPath="featuredfloorplan.view_all_cta"
                definition={viewAllCtaDef}
              >
                <a
                  href={viewAll.url || '/floor-plans'}
                  target={viewAll._target || '_self'}
                  className="ffp-link"
                >
                  <span>{viewAll.label || 'View all floor plans'}</span>
                  <span className="ffp-link-arrow" aria-hidden="true">→</span>
                </a>
              </EditableSlotGroup>
            </div>
          </div>
        </EditableCollectionBinding>

        {/* Editor-only hint for picking which plan is featured. Hidden on the
            live site because the value is injected into the pick logic, not
            rendered as copy. */}
        <EditableToken
          path="featuredfloorplan.featured_name"
          as="span"
          className="hidden"
          style={{ display: 'none' }}
        >
          {''}
        </EditableToken>
        {/* Empty-state anchor used only when there are no plans — keep static so
            the schema linter sees it even in the normal render path. */}
        <RichTextContent
          path="featuredfloorplan.empty_message"
          label="Empty Message"
          defaultValue=""
          as="span"
          className="hidden"
          style={{ display: 'none' }}
        />
        {/* 3D tour slot anchor — the live button only renders when a URL is
            set, so keep a hidden static anchor so the editor always surfaces
            the field in the sidebar. */}
        <EditableSlotGroup
          groupPath="featuredfloorplan.tour_3d"
          definition={tour3dDef}
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableSlotGroup>
      </div>

      <FeaturedFloorPlanStyles />
    </section>
  );
}

// ============================================================================
// Hidden schema-lint anchors (used by the empty-state branch above)
// ============================================================================

function HiddenAnchors() {
  return (
    <>
      <RichTextContent
        path="featuredfloorplan.description"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableToken
        path="featuredfloorplan.featured_name"
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      >
        {''}
      </EditableToken>
      <EditableSlotGroup
        groupPath="featuredfloorplan.schedule_cta"
        definition={scheduleCtaDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="featuredfloorplan.apply_cta"
        definition={applyCtaDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="featuredfloorplan.view_all_cta"
        definition={viewAllCtaDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="featuredfloorplan.tour_3d"
        definition={tour3dDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="featuredfloorplan.primary_image"
        definition={primaryImageDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="featuredfloorplan.image_2"
        definition={image2Def}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="featuredfloorplan.image_3"
        definition={image3Def}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
    </>
  );
}

// ============================================================================
// Scoped styles
// ============================================================================

function FeaturedFloorPlanStyles() {
  return (
    <style>{`
      .ffp-section {
        padding: 2.75rem 0 3.5rem;
        background: var(--vibe-color-background);
      }
      @media (min-width: 768px) {
        .ffp-section { padding: 3rem 0 3.75rem; }
      }
      @media (min-width: 1024px) {
        .ffp-section { padding: 4rem 0 5rem; }
      }
      .ffp-container {
        max-width: 1240px;
        margin: 0 auto;
        padding: 0 1rem;
      }
      @media (min-width: 1024px) { .ffp-container { padding: 0 1.5rem; } }

      .ffp-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 2rem;
        align-items: center;
      }
      /* Single-column layout stays until desktop. On tablet, cap the
         stacked column so the floor-plan image and heading don't blow out
         to full tablet width — matches how FloorplanShowcase constrains a
         lone card. */
      @media (min-width: 640px) and (max-width: 1023px) {
        .ffp-grid {
          max-width: 560px;
          margin-left: auto;
          margin-right: auto;
        }
      }
      @media (min-width: 1024px) {
        .ffp-grid {
          grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
          gap: 3.5rem;
        }
      }

      .ffp-media {
        position: relative;
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
      }
      /* Each image slot wrapper fills a 4:3 frame with the brand card
         treatment. Only one is visible at a time; the inactive siblings get
         .is-hidden and drop out of layout. */
      .ffp-media-main {
        position: relative;
        background: #ffffff;
        border: 1px solid #ECEDEF;
        border-radius: 6px;
        overflow: hidden;
        aspect-ratio: 4 / 3;
      }
      .ffp-media-main.is-hidden {
        display: none;
      }
      /* In edit mode, keep every slot visible so an editor can hover any of
         them to open its own sidebar. Stack them with a small gap so each
         thumbnail-sized frame remains clickable. */
      [data-vibesite-edit-mode="true"] .ffp-media-main.is-hidden {
        display: block;
        opacity: 0.55;
        aspect-ratio: 4 / 3;
        max-height: 7rem;
      }
      .ffp-media-image {
        width: 100%;
        height: 100%;
        object-fit: contain;
        padding: 1.25rem;
        display: block;
        transition: transform 0.6s ease-out;
      }
      .ffp-media-main:hover .ffp-media-image { transform: scale(1.03); }
      .ffp-media-placeholder {
        width: 100%;
        height: 100%;
        background: #F5F6F7;
      }
      .ffp-media-badge {
        position: absolute;
        top: 1rem;
        left: 1rem;
        z-index: 5;
        padding: 0.4rem 0.85rem;
        font-family: var(--vibe-font-body);
        font-size: 0.65rem;
        font-weight: 700;
        letter-spacing: 0.2em;
        text-transform: uppercase;
        color: var(--vibe-color-text-on-secondary);
        background: var(--vibe-color-secondary);
        border-radius: 2px;
      }

      /* Thumbnail navigator — appears only when 2+ images are set. */
      .ffp-thumbs {
        display: flex;
        gap: 0.5rem;
        padding-top: 0.25rem;
        justify-content: center;
      }
      @media (min-width: 1024px) {
        .ffp-thumbs { justify-content: flex-start; }
      }
      .ffp-thumb {
        position: relative;
        width: 68px;
        height: 68px;
        padding: 0;
        background: #ffffff;
        border: 1.5px solid #ECEDEF;
        border-radius: 4px;
        overflow: hidden;
        cursor: pointer;
        transition: border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
      }
      .ffp-thumb:hover {
        border-color: var(--vibe-color-primary);
        transform: translateY(-1px);
      }
      .ffp-thumb:focus-visible {
        outline: 2px solid var(--vibe-color-primary);
        outline-offset: 2px;
      }
      .ffp-thumb.is-active {
        border-color: var(--vibe-color-primary);
        box-shadow: 0 0 0 1px var(--vibe-color-primary) inset;
      }
      .ffp-thumb-image {
        width: 100%;
        height: 100%;
        object-fit: contain;
        padding: 0.3rem;
        display: block;
      }

      /* "View availability →" link below the gallery. */
      .ffp-media-detail-link {
        align-self: center;
        margin-top: 0.25rem;
        font-family: var(--vibe-font-heading);
        font-size: 0.68rem;
        font-weight: 600;
        letter-spacing: 0.2em;
        text-transform: uppercase;
        color: var(--vibe-color-text);
        text-decoration: underline;
        text-decoration-color: var(--vibe-color-primary);
        text-underline-offset: 5px;
        text-decoration-thickness: 1px;
        transition: text-decoration-thickness 0.2s ease;
      }
      @media (min-width: 1024px) {
        .ffp-media-detail-link { align-self: flex-start; }
      }
      .ffp-media-detail-link:hover { text-decoration-thickness: 2px; }

      .ffp-info {
        display: flex;
        flex-direction: column;
        gap: 1.1rem;
        text-align: center;
      }
      @media (min-width: 1024px) {
        .ffp-info { text-align: left; }
      }

      .ffp-name {
        font-family: var(--vibe-font-heading);
        /* Fluid, but tops out before desktop so tablet doesn't read oversized
           in the capped single-column layout. */
        font-size: clamp(1.85rem, 3.6vw, 2.25rem);
        font-weight: 400;
        line-height: 1.1;
        letter-spacing: 0.01em;
        color: var(--vibe-color-text);
        margin: 0;
      }
      @media (min-width: 1024px) {
        .ffp-name { font-size: clamp(2.1rem, 2.8vw, 3rem); }
      }

      .ffp-stats {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: center;
        gap: 0.5rem 1.25rem;
        padding: 1rem 0 0.25rem;
        border-top: 1px solid #ECEDEF;
        border-bottom: 1px solid #ECEDEF;
        padding: 1.1rem 0;
      }
      @media (min-width: 1024px) {
        .ffp-stats { justify-content: flex-start; }
      }
      .ffp-stat {
        display: inline-flex;
        align-items: baseline;
        gap: 0.4rem;
        color: var(--vibe-color-text);
      }
      .ffp-stat-value {
        font-family: var(--vibe-font-heading);
        font-size: 1.3rem;
        font-weight: 500;
        line-height: 1;
      }
      .ffp-stat-label {
        font-family: var(--vibe-font-body);
        font-size: 0.7rem;
        font-weight: 700;
        letter-spacing: 0.18em;
        text-transform: uppercase;
        color: var(--vibe-color-text-muted);
      }
      .ffp-stat-sep {
        display: inline-block;
        width: 1px;
        height: 1.4rem;
        background: #DADCDF;
      }

      .ffp-price {
        margin: 0;
        font-family: var(--vibe-font-body);
        font-size: 1rem;
        color: var(--vibe-color-text);
      }

      .ffp-description {
        font-family: var(--vibe-font-body);
        font-size: 0.95rem;
        line-height: 1.65;
        color: var(--vibe-color-text-muted);
      }
      .ffp-description p { margin: 0 0 0.6rem; }
      .ffp-description p:last-child { margin-bottom: 0; }
      .ffp-description a {
        color: var(--vibe-color-text);
        text-decoration: underline;
        text-decoration-color: var(--vibe-color-primary);
        text-underline-offset: 3px;
      }

      .ffp-actions {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 0.75rem;
        padding-top: 0.25rem;
      }
      @media (min-width: 1024px) {
        .ffp-actions { justify-content: flex-start; }
      }

      .ffp-cta {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 11.5rem;
        padding: 0.95rem 1.75rem;
        font-family: var(--vibe-font-heading);
        font-size: 0.72rem;
        font-weight: 600;
        letter-spacing: 0.2em;
        text-transform: uppercase;
      }

      /* Outline variant for the secondary CTA — keeps the brand accent as
         text + border instead of the filled sheen style. */
      .ffp-cta--outline {
        background: transparent;
        border: 1.5px solid var(--vibe-color-primary);
        border-radius: 2px;
        color: var(--vibe-color-text);
        text-decoration: none;
        transition: background-color 0.3s ease, color 0.3s ease, transform 0.3s ease;
      }
      .ffp-cta--outline:hover {
        background: var(--vibe-color-primary);
        color: var(--vibe-color-text-on-primary, #fff);
        transform: translateY(-1px);
      }

      /* 3D tour icon-button — compact, visually distinct from the two primary
         CTAs. Keeps the same height so the row stays aligned. */
      .ffp-tour-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        padding: 0.85rem 1.1rem;
        border: 1.5px solid #DADCDF;
        border-radius: 2px;
        background: #ffffff;
        color: var(--vibe-color-text);
        font-family: var(--vibe-font-heading);
        font-size: 0.72rem;
        font-weight: 600;
        letter-spacing: 0.2em;
        text-transform: uppercase;
        text-decoration: none;
        transition: border-color 0.25s ease, color 0.25s ease, transform 0.25s ease, box-shadow 0.25s ease;
      }
      .ffp-tour-btn:hover {
        border-color: var(--vibe-color-primary);
        color: var(--vibe-color-primary);
        transform: translateY(-1px);
        box-shadow: 0 6px 14px rgba(0, 0, 0, 0.06);
      }
      .ffp-tour-btn:focus-visible {
        outline: 2px solid var(--vibe-color-primary);
        outline-offset: 2px;
      }
      .ffp-tour-icon {
        width: 1.15rem;
        height: 1.15rem;
        flex-shrink: 0;
        display: block;
      }
      .ffp-tour-label {
        display: inline-block;
      }
      /* On very narrow screens, drop the label and show the icon alone so
         three buttons in a row don't wrap awkwardly. aria-label on the
         anchor keeps it accessible. */
      @media (max-width: 420px) {
        .ffp-tour-btn {
          padding: 0.85rem 0.95rem;
          min-width: 0;
        }
        .ffp-tour-label {
          display: none;
        }
      }

      .ffp-link {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        margin-top: 0.5rem;
        font-family: var(--vibe-font-heading);
        font-size: 0.72rem;
        font-weight: 600;
        letter-spacing: 0.2em;
        text-transform: uppercase;
        color: var(--vibe-color-text);
        text-decoration: underline;
        text-decoration-color: var(--vibe-color-primary);
        text-underline-offset: 6px;
        text-decoration-thickness: 1px;
        transition: text-decoration-thickness 0.2s ease;
      }
      @media (min-width: 1024px) {
        .ffp-link { justify-content: flex-start; }
      }
      .ffp-link:hover { text-decoration-thickness: 2px; }
      .ffp-link-arrow {
        display: inline-block;
        transition: transform 0.3s ease;
      }
      .ffp-link:hover .ffp-link-arrow { transform: translateX(3px); }

      .ffp-empty {
        font-family: var(--vibe-font-body);
        font-size: 1rem;
        text-align: center;
        color: var(--vibe-color-text-muted);
        padding: 2rem 1rem;
      }
      .ffp-empty p { margin: 0; }
    `}</style>
  );
}

// ============================================================================
// Content schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'FeaturedFloorPlan',
  displayName: 'Featured Floor Plan',
  tokens: {
    'featuredfloorplan.description': {
      label: 'Description',
      type: 'richtext',
      defaultValue:
        '<p>Thoughtfully laid out with the finishes residents love most, this plan brings together open living space, a dedicated kitchen, and comfortable bedrooms. Tour it in person or apply online to make it yours.</p>',
      description:
        'Short pitch for the featured plan — appears beneath the specs and above the CTAs.',
    },
    'featuredfloorplan.featured_name': {
      label: 'Featured Plan Name (overrides auto pick)',
      type: 'text',
      defaultValue: '',
      description:
        'Optional. Type a floor plan name or code (e.g. "A1") to feature that plan instead of the auto-picked one.',
    },
    'featuredfloorplan.empty_message': {
      label: 'Empty Message',
      type: 'richtext',
      defaultValue:
        '<p>We haven’t featured a floor plan yet. Explore all of our floor plans or contact our leasing team for upcoming availability.</p>',
      description: 'Shown when the property has no enabled floor plans.',
    },
  },
  slotGroups: {
    'featuredfloorplan.primary_image': primaryImageDef,
    'featuredfloorplan.image_2': image2Def,
    'featuredfloorplan.image_3': image3Def,
    'featuredfloorplan.schedule_cta': scheduleCtaDef,
    'featuredfloorplan.apply_cta': applyCtaDef,
    'featuredfloorplan.view_all_cta': viewAllCtaDef,
    'featuredfloorplan.tour_3d': tour3dDef,
  },
  collectionBindings: {
    'featuredfloorplan.items': {
      label: 'Floor Plans',
      collection: 'floorplans',
      description:
        'Source list of floor plans. The featured plan is picked from here (most available units → biggest → first enabled), or by name via the Featured Plan Name token.',
    },
  },
};

(FeaturedFloorPlan as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default FeaturedFloorPlan;
